// ========== LOG & EXPONENTIAL FORM ==========
// Dual-space exploration: Log Space (left) and Exp Space (right).
// Students "bop" expressions between spaces using LogRewriteEquationAction
// and LogarithmInvertAction, making the inverse relationship spatially visible.

const PAGE_ID = 'log-exponential-form';

// ========== STATE ==========
let page;
let canvas;
let chainManager = null;
let expressions = [];
let completedSteps = new Set();
let affordanceSidebar = null;
let startChooser = null;
let rewriteFlow = null;

// Preset cycling state
const presetTemplateIndex = {};
let lastPresetId = null;

// Track which space the current expression is in: 'log' or 'exp'
let currentSpace = 'log';
// Persist the detected base across the session so it doesn't revert to '10'
let sessionBase = null;
// When true, sessionBase was explicitly set by user and takes priority over auto-detection
let userSetBase = false;
// Track the active preset for goal-aware success detection
let activePresetId = null;

// Shared helpers
const { showInfoMessage, markCanvasHasContent, markCanvasEmpty, StartChooser } =
  GuidedTopics;
const { ModalCanvas } = GuidedTopics.Workflows;
const { hasRemainingOpportunities } = GuidedTopics;

// ========== CONSTANTS ==========
const START_Y = 50; // Y position for original expression
const STEP_SPACING = 70; // Vertical spacing between chain steps

// Dynamic X positions relative to the canvas width
function getCanvasWidth() {
  const container = document.querySelector('.canvas-container');
  return container ? container.clientWidth : 800;
}
function getLogSpaceX() {
  return Math.max(30, getCanvasWidth() * 0.08);
}
function getExpSpaceX() {
  return Math.max(200, getCanvasWidth() * 0.55);
}

// ========== ACTION DETECTION ==========
const ACTION_TO_LABEL = {
  LogarithmInvertAction: 'Convert', // disambiguated below
  LogarithmEvaluateAction: 'Evaluate',
  LogarithmEvaluateSameArgAsBaseAction: 'Evaluate',
  LogSimplifyAction: 'Simplify',
  LogSimplifyPowerAction: 'Simplify',
  LogRewriteEquationAction: 'Apply Log', // bop to log space
  CalculatePowerAction: 'Calculate',
  AddSubNumbersAction: 'Simplify',
  MultiplyNumbersAction: 'Simplify',
};

// Always allowed actions (arithmetic helpers)
const ALWAYS_ALLOWED = new Set([
  'AddSubNumbersAction',
  'MultiplyNumbersAction',
  'MultiplyPowersAction',
  'FractionCancelPowersAction',
  'CalculatePowerAction',
]);

// Core allowed actions for this page
const PAGE_ALLOWED = new Set([
  'LogarithmInvertAction',
  'LogarithmEvaluateAction',
  'LogarithmEvaluateSameArgAsBaseAction',
  'LogSimplifyAction',
  'LogSimplifyPowerAction',
]);

// Blocked actions (log property rules — belong to log-properties page)
const ALWAYS_BLOCKED = new Set([
  'LogarithmSplitAction',
  'AddSubLogarithmsAction',
  'LogarithmExtractExponentOfArgumentAction',
  'MulDivLogarithmsAction',
  'LogCombineBaseAction',
  'LogRewriteEquationAction',
  'LogRewriteEquationIdAction',
]);

/**
 * Detect which action was applied in a transaction and return a step label.
 */
function detectAction(transaction, prevAscii, newAscii) {
  if (!transaction || prevAscii === newAscii) return null;
  const action = transaction.action;
  if (!action) return null;
  const mathActions = action.actions || [];
  for (const ma of mathActions) {
    if (!ma.name) continue;
    const label = ACTION_TO_LABEL[ma.name];
    if (!label) continue;

    // Disambiguate LogarithmInvertAction direction
    if (ma.name === 'LogarithmInvertAction') {
      const hasLogResult = /log[_\d(]|ln\(/.test(newAscii);
      const hadLog = /log[_\d(]|ln\(/.test(prevAscii);
      if (hadLog && !hasLogResult) return 'Convert to Exp Form';
      if (!hadLog && hasLogResult) return 'Convert to Log Form';
      return 'Convert';
    }

    return label;
  }
  return null;
}

/**
 * Determine which space an expression string belongs in.
 * Heuristic: if it contains 'log' or 'ln(', it's log space; otherwise exp space.
 */
function detectSpace(ascii) {
  if (!ascii) return 'log';
  // If expression contains a log function, it's in log space
  if (/\blog[_\d(]|\bln\(/.test(ascii)) return 'log';
  // If it contains an exponent pattern like b^x, it's exp space
  if (/\^/.test(ascii)) return 'exp';
  // Numbers/variables default to exp space (result of evaluation)
  return 'exp';
}

/**
 * Get the X position for a given space.
 */
function getXForSpace(space) {
  return space === 'log' ? getLogSpaceX() : getExpSpaceX();
}

/**
 * Animate a derivation's x-position from its current position to a target.
 * Also animates the label button in sync.
 */
function animateDerivationX(derivation, labelButton, targetX, durationMs = 400) {
  const startX = derivation.pos.x;
  if (Math.abs(startX - targetX) < 2) {
    derivation.pos.x = targetX;
    if (labelButton) {
      labelButton.parentElement.style.left = targetX + chainManager.labelX + 'px';
    }
    return;
  }
  const startTime = performance.now();
  function step(now) {
    const elapsed = now - startTime;
    const t = Math.min(elapsed / durationMs, 1);
    // Ease-out cubic
    const eased = 1 - Math.pow(1 - t, 3);
    const x = startX + (targetX - startX) * eased;
    derivation.pos.x = x;
    if (labelButton) {
      labelButton.parentElement.style.left = x + chainManager.labelX + 'px';
    }
    if (t < 1) requestAnimationFrame(step);
  }
  requestAnimationFrame(step);
}

/**
 * Reposition all expressions and their label buttons to match current canvas width.
 * Called on window resize so the dual-zone layout stays correct.
 */
function repositionAllExpressions() {
  if (!chainManager) return;

  // Reposition original expression
  if (expressions.length > 0) {
    const expr = expressions[0];
    const space = expr.space || currentSpace;
    const xPos = getXForSpace(space);
    if (expr.derivation) {
      expr.derivation.pos.x = xPos;
      if (expr.labelButton) {
        expr.labelButton.parentElement.style.left = xPos + chainManager.labelX + 'px';
      }
    }
  }

  // Reposition chain steps
  for (const step of chainManager.steps) {
    const space = step.space || currentSpace;
    const xPos = getXForSpace(space);
    if (step.derivation) {
      step.derivation.pos.x = xPos;
      if (step.labelButton) {
        step.labelButton.parentElement.style.left = xPos + chainManager.labelX + 'px';
      }
    }
  }
}

// ========== CLICK OVERLAYS ==========
// Place transparent click-catching divs over each expression so clicking
// on the math opens the Rewrite Formula modal.
function updateClickOverlays() {
  const container = document.getElementById('gm-page-container');
  if (!container) return;
  container.querySelectorAll('.expression-click-overlay').forEach((el) => el.remove());
  if (!canvas?.model) return;

  const allDLs = canvas.model.dls?.() || [];
  for (const dl of allDLs) {
    if (!dl.pos || !dl.size || dl.size.width < 10) continue;
    const overlay = document.createElement('div');
    overlay.className = 'expression-click-overlay';
    overlay.style.cssText = 'position:absolute;cursor:pointer;z-index:2;';
    overlay.style.left = dl.pos.x + 'px';
    overlay.style.top = dl.pos.y + 'px';
    overlay.style.width = dl.size.width + 'px';
    overlay.style.height = dl.size.height + 'px';
    container.appendChild(overlay);
  }
}

// ========== ACTION FILTER ==========
// Block all GM actions on the main canvas — algebra happens in the Rewrite modal
function getActionFilter() {
  return () => false;
}

function applyActionFilter(derivation) {
  const filter = getActionFilter();
  const model = derivation.getLastRow()?.model;
  if (model) {
    model.settings.set('actionFilter', filter);
  }
}

function applyActionFilterToAll() {
  const filter = getActionFilter();
  for (const expr of expressions) {
    if (expr.derivation) {
      const model = expr.derivation.getLastRow()?.model;
      if (model) model.settings.set('actionFilter', filter);
    }
  }
  if (chainManager?.steps) {
    for (const step of chainManager.steps) {
      if (step.derivation) {
        const model = step.derivation.getLastRow()?.model;
        if (model) model.settings.set('actionFilter', filter);
      }
    }
  }
}

// ========== PRESET ICONS ==========
const PRESET_ICONS = {
  // Convert to Log Form: exponential equation with downward arrow to log form
  'to-log-basic': `<svg viewBox="0 0 60 48" fill="none" stroke="#374151" stroke-width="1.5">
    <text x="6" y="18" font-size="10" font-family="serif" fill="#374151" stroke="none">2\u2076 = 64</text>
    <path d="M25 24 L25 32" stroke-dasharray="3 2"/>
    <text x="2" y="44" font-size="8" font-family="serif" fill="#374151" stroke="none">log\u2082(64)=6</text>
  </svg>`,
  'to-log-neg': `<svg viewBox="0 0 60 48" fill="none" stroke="#374151" stroke-width="1.5">
    <text x="3" y="18" font-size="9" font-family="serif" fill="#374151" stroke="none">4\u207B\u00B2=1/16</text>
    <path d="M25 24 L25 32" stroke-dasharray="3 2"/>
    <text x="0" y="44" font-size="7" font-family="serif" fill="#374151" stroke="none">log\u2084(1/16)</text>
  </svg>`,
  // Convert to Exp Form: log equation with arrow to exponential
  'to-exp-basic': `<svg viewBox="0 0 60 48" fill="none" stroke="#374151" stroke-width="1.5">
    <text x="2" y="18" font-size="9" font-family="serif" fill="#374151" stroke="none">log\u2087(49)=2</text>
    <path d="M25 24 L25 32" stroke-dasharray="3 2"/>
    <text x="8" y="44" font-size="10" font-family="serif" fill="#374151" stroke="none">7\u00B2=49</text>
  </svg>`,
  'to-exp-common': `<svg viewBox="0 0 60 48" fill="none" stroke="#374151" stroke-width="1.5">
    <text x="1" y="18" font-size="9" font-family="serif" fill="#374151" stroke="none">log(1000)=3</text>
    <path d="M25 24 L25 32" stroke-dasharray="3 2"/>
    <text x="4" y="44" font-size="9" font-family="serif" fill="#374151" stroke="none">10\u00B3=1000</text>
  </svg>`,
  // Evaluate: log expression with arrow to number
  'eval-integer': `<svg viewBox="0 0 60 48" fill="none" stroke="#374151" stroke-width="1.5">
    <text x="4" y="20" font-size="11" font-family="serif" fill="#374151" stroke="none">log</text>
    <text x="24" y="24" font-size="8" font-family="serif" fill="#374151" stroke="none">3</text>
    <text x="28" y="20" font-size="11" font-family="serif" fill="#374151" stroke="none">(81)</text>
    <path d="M25 26 L25 34" stroke-dasharray="3 2"/>
    <text x="21" y="44" font-size="12" font-family="serif" fill="#374151" stroke="none">4</text>
  </svg>`,
  'eval-common': `<svg viewBox="0 0 60 48" fill="none" stroke="#374151" stroke-width="1.5">
    <text x="4" y="20" font-size="10" font-family="serif" fill="#374151" stroke="none">log(1000)</text>
    <path d="M25 26 L25 34" stroke-dasharray="3 2"/>
    <text x="21" y="44" font-size="12" font-family="serif" fill="#374151" stroke="none">3</text>
  </svg>`,
  'eval-negative': `<svg viewBox="0 0 60 48" fill="none" stroke="#374151" stroke-width="1.5">
    <text x="2" y="20" font-size="9" font-family="serif" fill="#374151" stroke="none">log\u2084(1/16)</text>
    <path d="M25 26 L25 34" stroke-dasharray="3 2"/>
    <text x="19" y="44" font-size="12" font-family="serif" fill="#374151" stroke="none">\u22122</text>
  </svg>`,
  'eval-fraction': `<svg viewBox="0 0 60 48" fill="none" stroke="#374151" stroke-width="1.5">
    <text x="4" y="20" font-size="10" font-family="serif" fill="#374151" stroke="none">log\u2089(3)</text>
    <path d="M25 26 L25 34" stroke-dasharray="3 2"/>
    <text x="19" y="44" font-size="11" font-family="serif" fill="#374151" stroke="none">1/2</text>
  </svg>`,
  // Special Values
  'special-zero': `<svg viewBox="0 0 60 48" fill="none" stroke="#374151" stroke-width="1.5">
    <text x="4" y="20" font-size="10" font-family="serif" fill="#374151" stroke="none">log\u2085(1)</text>
    <path d="M25 26 L25 34" stroke-dasharray="3 2"/>
    <text x="21" y="44" font-size="12" font-family="serif" fill="#374151" stroke="none">0</text>
  </svg>`,
  'special-inverse': `<svg viewBox="0 0 60 48" fill="none" stroke="#374151" stroke-width="1.5">
    <text x="6" y="20" font-size="10" font-family="serif" fill="#374151" stroke="none">ln(e\u2075)</text>
    <path d="M25 26 L25 34" stroke-dasharray="3 2"/>
    <text x="21" y="44" font-size="12" font-family="serif" fill="#374151" stroke="none">5</text>
  </svg>`,
  'special-inverse-exp': `<svg viewBox="0 0 60 48" fill="none" stroke="#374151" stroke-width="1.5">
    <text x="2" y="20" font-size="9" font-family="serif" fill="#374151" stroke="none">10^(log 6)</text>
    <path d="M25 26 L25 34" stroke-dasharray="3 2"/>
    <text x="21" y="44" font-size="12" font-family="serif" fill="#374151" stroke="none">6</text>
  </svg>`,
  'special-identity': `<svg viewBox="0 0 60 48" fill="none" stroke="#374151" stroke-width="1.5">
    <text x="4" y="20" font-size="10" font-family="serif" fill="#374151" stroke="none">log\u2087(7)</text>
    <path d="M25 26 L25 34" stroke-dasharray="3 2"/>
    <text x="21" y="44" font-size="12" font-family="serif" fill="#374151" stroke="none">1</text>
  </svg>`,
};

// ========== PRESETS ==========
const PAGE_PRESETS = {
  // Convert to Log Form
  'preset-to-log-basic': {
    id: 'preset-to-log-basic',
    name: '2\u2076 = 64',
    icon: PRESET_ICONS['to-log-basic'],
    expressions: [],
    expressionTemplates: ['2^6 = 64', '5^3 = 125', '10^4 = 10000'],
    startSpace: 'exp',
    goalSpace: 'log',
  },
  'preset-to-log-neg': {
    id: 'preset-to-log-neg',
    name: '4\u207B\u00B2 = 1/16',
    icon: PRESET_ICONS['to-log-neg'],
    expressions: [],
    expressionTemplates: ['4^{-2} = 1/16', '3^{-3} = 1/27', '10^{-1} = 1/10'],
    startSpace: 'exp',
    goalSpace: 'log',
  },
  // Convert to Exp Form
  'preset-to-exp-basic': {
    id: 'preset-to-exp-basic',
    name: 'log\u2087(49) = 2',
    icon: PRESET_ICONS['to-exp-basic'],
    expressions: [],
    expressionTemplates: ['log_7(49) = 2', 'log_2(32) = 5', 'log_3(243) = 5'],
    startSpace: 'log',
    goalSpace: 'exp',
  },
  'preset-to-exp-common': {
    id: 'preset-to-exp-common',
    name: 'log(1000) = 3',
    icon: PRESET_ICONS['to-exp-common'],
    expressions: [],
    expressionTemplates: ['log(1000) = 3', 'log(0.01) = -2', 'ln(1) = 0'],
    lnTemplates: ['ln(1) = 0'],
    startSpace: 'log',
    goalSpace: 'exp',
  },
  // Evaluate
  'preset-eval-integer': {
    id: 'preset-eval-integer',
    name: 'log\u2083(81) = x',
    icon: PRESET_ICONS['eval-integer'],
    expressions: [],
    expressionTemplates: ['log_3(81) = x', 'log_2(64) = x', 'log_5(625) = x'],
    startSpace: 'log',
  },
  'preset-eval-common': {
    id: 'preset-eval-common',
    name: 'log(1000) = x',
    icon: PRESET_ICONS['eval-common'],
    expressions: [],
    expressionTemplates: ['log(1000) = x', 'log(100) = x', 'log(10) = x'],
    startSpace: 'log',
  },
  'preset-eval-negative': {
    id: 'preset-eval-negative',
    name: 'log\u2084(1/16) = x',
    icon: PRESET_ICONS['eval-negative'],
    expressions: [],
    expressionTemplates: ['log_4(1/16) = x', 'log_2(1/8) = x', 'log_3(1/27) = x'],
    startSpace: 'log',
  },
  'preset-eval-fraction': {
    id: 'preset-eval-fraction',
    name: 'log\u2089(3) = x',
    icon: PRESET_ICONS['eval-fraction'],
    expressions: [],
    expressionTemplates: ['log_9(3) = x', 'log_4(2) = x', 'log_8(2) = x'],
    startSpace: 'log',
  },
  // Special Values
  'preset-special-zero': {
    id: 'preset-special-zero',
    name: 'log\u2085(1) = x',
    icon: PRESET_ICONS['special-zero'],
    expressions: [],
    expressionTemplates: ['log_5(1) = x', 'log_3(1) = x', 'log(1) = x'],
    startSpace: 'log',
  },
  'preset-special-inverse': {
    id: 'preset-special-inverse',
    name: 'ln(e\u2075) = x',
    icon: PRESET_ICONS['special-inverse'],
    expressions: [],
    expressionTemplates: ['ln(e^5) = x', 'ln(e^3) = x', 'log_2(2^7) = x'],
    lnTemplates: ['ln(e^5) = x', 'ln(e^3) = x'],
    startSpace: 'log',
  },
  'preset-special-inverse-exp': {
    id: 'preset-special-inverse-exp',
    name: '10^(log 6) = x',
    icon: PRESET_ICONS['special-inverse-exp'],
    expressions: [],
    expressionTemplates: ['10^{log(6)} = x', 'e^{ln(4)} = x', '2^{log_2(9)} = x'],
    lnTemplates: ['e^{ln(4)} = x'],
    startSpace: 'exp',
  },
  'preset-special-identity': {
    id: 'preset-special-identity',
    name: 'log\u2087(7) = x',
    icon: PRESET_ICONS['special-identity'],
    expressions: [],
    expressionTemplates: ['log_7(7) = x', 'log_2(2) = x', 'log_5(5) = x'],
    lnTemplates: ['ln(e) = x'],
    startSpace: 'log',
  },
};

/** Get effective templates for a preset, filtering ln if disabled. */
function getEffectiveTemplates(presetId) {
  const preset = PAGE_PRESETS[presetId];
  if (!preset) return [];
  const base = preset.expressionTemplates || [];
  const allowLn = document.getElementById('setting-allow-ln')?.checked ?? true;
  if (allowLn) return [...base];
  return base.filter((t) => !t.includes('ln(') && !t.includes('ln '));
}

// ========== CHAIN HELPERS ==========
function getCurrentExpressionAscii() {
  return chainManager?.getCurrentAscii();
}

function addChainStep(newAscii, reasonText, space) {
  if (!canvas?.model) return;
  const showLabels = document.getElementById('setting-show-labels')?.checked !== false;

  // Determine the space for positioning
  const resolvedSpace = space || detectSpace(newAscii);
  currentSpace = resolvedSpace;
  const xPos = getXForSpace(resolvedSpace);

  chainManager.addStep(newAscii, showLabels ? reasonText : null, (stepData) => {
    stepData.rule = reasonText;
    stepData.space = resolvedSpace;

    // Animate derivation and its label button to the correct zone
    if (stepData.derivation) {
      animateDerivationX(stepData.derivation, stepData.labelButton, xPos);
    }

    // Position reason label relative to expression
    if (stepData.reasonEl) {
      const exprWidth = stepData.derivation?.size?.width || 200;
      stepData.reasonEl.style.left = xPos + exprWidth + 20 + 'px';
    }

    applyActionFilter(stepData.derivation);

    // Wire up manipulation detection
    stepData.derivation.timeline.on('newLocalTransaction', (transaction) => {
      const newModel = stepData.derivation.getLastRow()?.model;
      if (newModel) newModel.settings.set('actionFilter', getActionFilter());
      setTimeout(() => onUserManipulation(stepData, transaction), 100);
    });

    updateButtonStates();
  });
}

// ========== PRESET HELPERS ==========
function setupExpressionFromPreset(presetId) {
  const templates = getEffectiveTemplates(presetId);
  if (!templates.length) return;

  if (presetId === lastPresetId) {
    presetTemplateIndex[presetId] =
      ((presetTemplateIndex[presetId] || 0) + 1) % templates.length;
  } else {
    if (!(presetId in presetTemplateIndex)) {
      presetTemplateIndex[presetId] = 0;
    }
  }
  lastPresetId = presetId;
  activePresetId = presetId;

  const idx = presetTemplateIndex[presetId] % templates.length;
  const preset = PAGE_PRESETS[presetId];
  const space = preset?.startSpace || detectSpace(templates[idx]);
  createOriginalExpression(templates[idx], space);
}

function createOriginalExpression(expressionAscii, space) {
  startChooser?.hide();
  if (!canvas || !canvas.model) {
    showInfoMessage('Please wait for the canvas to load.');
    return;
  }

  // Reset page state
  completedSteps.clear();
  sessionBase = null; // Reset base for new expression
  userSetBase = false;
  currentSpace = space || detectSpace(expressionAscii);
  document.getElementById('success-indicator')?.classList.remove('visible');

  const xPos = getXForSpace(currentSpace);

  chainManager.createOriginalExpression(expressionAscii, {
    expressionTracker: expressions,
    onCreated: (exprData, createdDL) => {
      // Position in the correct zone
      createdDL.pos.x = xPos;
      if (exprData.labelButton) {
        exprData.labelButton.parentElement.style.left = xPos + chainManager.labelX + 'px';
      }

      exprData.space = currentSpace;

      applyActionFilterToAll();

      // Wire up manipulation detection
      createdDL.timeline.on('newLocalTransaction', (transaction) => {
        const newModel = createdDL.getLastRow()?.model;
        if (newModel) newModel.settings.set('actionFilter', getActionFilter());
        setTimeout(() => onUserManipulation(exprData, transaction), 100);
      });

      markCanvasHasContent();
      updateButtonStates();
    },
  });
}

// ========== USER MANIPULATION DETECTION ==========
function onUserManipulation(expr, transaction) {
  const lastRow = expr.derivation.getLastRow();
  if (!lastRow) return;

  const newAscii = lastRow.model.to_ascii();
  const prevAscii = getCurrentExpressionAscii();
  if (newAscii === prevAscii) return;

  const ruleName = detectAction(transaction, prevAscii, newAscii);
  if (!ruleName) return;

  // Detect which space the result belongs to
  const newSpace = detectSpace(newAscii);
  currentSpace = newSpace;

  const showLabels = document.getElementById('setting-show-labels')?.checked !== false;

  const yPos =
    lastRow.pos?.y != null
      ? lastRow.pos.y + (lastRow.size?.height || 36) / 2 - 10
      : undefined;

  chainManager.recordManualStep(newAscii, showLabels ? ruleName : null, yPos);
  const lastStep = chainManager.steps[chainManager.steps.length - 1];
  if (lastStep) {
    lastStep.rule = ruleName;
    lastStep.space = newSpace;
    lastStep.prevAscii = prevAscii;
    // Position reason label relative to expression
    if (lastStep.reasonEl) {
      const exprX = getXForSpace(newSpace);
      const exprWidth = expr.derivation?.size?.width || 200;
      lastStep.reasonEl.style.left = exprX + exprWidth + 20 + 'px';
    }
  }

  applyActionFilterToAll();
  checkFullySimplified();
  updateButtonStates();
}

// ========== BOP OPERATIONS ==========

/**
 * Get the latest derivation (from chain steps or original expression).
 */
function getLatestDerivation() {
  if (chainManager?.steps) {
    for (let i = chainManager.steps.length - 1; i >= 0; i--) {
      if (chainManager.steps[i].derivation) return chainManager.steps[i].derivation;
    }
  }
  return expressions[0]?.derivation || null;
}

/**
 * Detect the base from the current expression.
 * Walks the expression tree looking for log bases or exponential bases.
 */
function detectBase(model) {
  if (!model) return '10';
  // Unwrap AlgebraModel container
  let root = model;
  if (root.children?.length === 1 && !root.group_name) root = root.children[0];

  let found = null;
  function walk(node) {
    if (!node || found) return;
    // Check for log function with explicit base
    if (node.is_group?.('func') && (node.value === 'log' || node.value === 'ln')) {
      if (node.value === 'ln') {
        found = 'e';
        return;
      }
      const label = node.children?.[0];
      const ss = label?.get_script?.('subscript')?.children?.[0];
      if (ss) {
        found = ss.to_ascii();
        return;
      }
      found = '10'; // log without subscript = base 10
      return;
    }
    // Check for power/exponent (b^x)
    if (node.is_group?.('power') || node.is_group?.('exponent')) {
      const base = node.children?.[0];
      if (base) {
        found = base.to_ascii();
        return;
      }
    }
    if (node.children) node.children.forEach(walk);
  }
  walk(root);
  return found || '10';
}

/**
 * "Shift to Exp Space": Automatically wrap expression/equation in b^(...).
 * For equations: LHS = RHS → b^(LHS) = b^(RHS)
 * For expressions: E → b^(E)
 */
/**
 * Resolve the base for a bop operation.
 * If user explicitly set a base, use that. Otherwise auto-detect from expression.
 */
function resolveBopBase() {
  // User-set base takes priority
  if (userSetBase && sessionBase) return sessionBase;
  // Auto-detect from expression
  const derivation = getLatestDerivation();
  const model = derivation?.getLastRow()?.model;
  const detected = detectBase(model);
  const base = (detected !== '10' ? detected : null) || sessionBase || detected;
  sessionBase = base;
  return base;
}

/**
 * Let the user set the base manually via a styled modal.
 * Updates sessionBase and refreshes bop button labels.
 */
function promptSetBase() {
  const current = sessionBase || resolveBopBase();

  // Create overlay
  const overlay = document.createElement('div');
  overlay.style.cssText =
    'position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.35);z-index:1000;display:flex;align-items:center;justify-content:center;';

  const card = document.createElement('div');
  card.style.cssText =
    'background:#fff;border-radius:12px;padding:1.5rem 2rem;box-shadow:0 8px 24px rgba(0,0,0,0.15);min-width:280px;max-width:360px;font-family:-apple-system,BlinkMacSystemFont,sans-serif;';

  const title = document.createElement('h3');
  title.textContent = 'Set Base';
  title.style.cssText =
    'margin:0 0 0.75rem;font-size:1.1rem;font-weight:600;color:#1f2937;';
  card.appendChild(title);

  const desc = document.createElement('p');
  desc.textContent = 'Choose the base for shifting between log and exponential space.';
  desc.style.cssText = 'margin:0 0 1rem;font-size:0.85rem;color:#6b7280;line-height:1.4;';
  card.appendChild(desc);

  // Quick-pick buttons for common bases
  const quickRow = document.createElement('div');
  quickRow.style.cssText = 'display:flex;gap:0.5rem;margin-bottom:1rem;';
  const commonBases = ['2', '3', '5', '7', '10', 'e'];
  for (const b of commonBases) {
    const btn = document.createElement('button');
    btn.textContent = b;
    btn.style.cssText =
      'font-family:Kalam,Georgia,serif;font-size:1.1rem;font-weight:600;padding:0.4rem 0.7rem;border-radius:8px;border:2px solid ' +
      (b === current ? '#7c3aed' : '#e5e7eb') +
      ';background:' +
      (b === current ? '#ede9fe' : '#f9fafb') +
      ';color:' +
      (b === current ? '#5b21b6' : '#374151') +
      ';cursor:pointer;transition:all 0.15s;';
    btn.addEventListener('mouseenter', () => {
      btn.style.borderColor = '#7c3aed';
      btn.style.background = '#ede9fe';
    });
    btn.addEventListener('mouseleave', () => {
      if (b !== current) {
        btn.style.borderColor = '#e5e7eb';
        btn.style.background = '#f9fafb';
      }
    });
    btn.addEventListener('click', () => {
      sessionBase = b;
      userSetBase = true;
      updateButtonStates();
      overlay.remove();
    });
    quickRow.appendChild(btn);
  }
  card.appendChild(quickRow);

  // Custom input for other bases
  const inputRow = document.createElement('div');
  inputRow.style.cssText =
    'display:flex;gap:0.5rem;align-items:center;margin-bottom:1rem;';
  const label = document.createElement('span');
  label.textContent = 'Other:';
  label.style.cssText = 'font-size:0.85rem;color:#6b7280;';
  inputRow.appendChild(label);
  const input = document.createElement('input');
  input.type = 'text';
  input.placeholder = 'e.g. 4';
  input.style.cssText =
    'flex:1;font-family:Kalam,Georgia,serif;font-size:1rem;padding:0.4rem 0.6rem;border:2px solid #e5e7eb;border-radius:8px;outline:none;';
  input.addEventListener('focus', () => {
    input.style.borderColor = '#7c3aed';
  });
  input.addEventListener('blur', () => {
    input.style.borderColor = '#e5e7eb';
  });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      const val = input.value.trim();
      if (val) {
        sessionBase = val;
        userSetBase = true;
        updateButtonStates();
        overlay.remove();
      }
    }
  });
  inputRow.appendChild(input);
  const goBtn = document.createElement('button');
  goBtn.textContent = 'Set';
  goBtn.style.cssText =
    'font-size:0.85rem;font-weight:600;padding:0.4rem 0.8rem;border-radius:8px;border:none;background:#7c3aed;color:#fff;cursor:pointer;';
  goBtn.addEventListener('click', () => {
    const val = input.value.trim();
    if (val) {
      sessionBase = val;
      userSetBase = true;
      updateButtonStates();
      overlay.remove();
    }
  });
  inputRow.appendChild(goBtn);
  card.appendChild(inputRow);

  // Cancel
  const cancelBtn = document.createElement('button');
  cancelBtn.textContent = 'Cancel';
  cancelBtn.style.cssText =
    'width:100%;padding:0.5rem;border:1px solid #e5e7eb;border-radius:8px;background:#f9fafb;color:#6b7280;cursor:pointer;font-size:0.85rem;';
  cancelBtn.addEventListener('click', () => overlay.remove());
  card.appendChild(cancelBtn);

  overlay.appendChild(card);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) overlay.remove();
  });
  document.body.appendChild(overlay);

  // Focus input after animation
  requestAnimationFrame(() => input.focus());
}

function bopToExpSpace() {
  const currentAscii = getCurrentExpressionAscii();
  if (!currentAscii) {
    showInfoMessage('Create an expression first.');
    return;
  }

  const useModal = document.getElementById('setting-bop-modal')?.checked;
  if (useModal) {
    bopViaModal(currentAscii, 'exp');
    return;
  }

  const base = resolveBopBase();

  // Try smart shift: if all terms are log_b(...), just extract arguments
  const derivation = getLatestDerivation();
  const model = derivation?.getLastRow()?.model;
  if (model && currentAscii.includes('=')) {
    const eqNode = findEqualsNode(model);
    if (eqNode && eqNode.children?.length >= 2) {
      const lhsNode = eqNode.children[0];
      const rhsNode = eqNode.children[eqNode.children.length - 1];
      const lhsArg = extractLogArgIfBaseMatches(lhsNode, base);
      const rhsArg = extractLogArgIfBaseMatches(rhsNode, base);
      if (lhsArg !== null && rhsArg !== null) {
        addChainStep(lhsArg + '=' + rhsArg, 'Shift to Exp Space', 'exp');
        return;
      }
    }
  }

  // Fallback: wrap in b^{...}
  let newAscii;
  if (currentAscii.includes('=')) {
    const [lhs, rhs] = currentAscii.split('=').map((s) => s.trim());
    newAscii = base + '^{' + lhs + '}=' + base + '^{' + rhs + '}';
  } else {
    newAscii = base + '^{' + currentAscii + '}';
  }

  addChainStep(newAscii, 'Shift to Exp Space', 'exp');
}

/**
 * Check if a model node is a log function with the given base.
 * Returns the argument's ASCII if it matches, or null.
 * Unwraps container nodes (AlgebraModel, brackets) to find the log.
 */
function extractLogArgIfBaseMatches(node, baseAscii) {
  if (!node) return null;
  while (
    node.children?.length === 1 &&
    !node.is_group?.('func') &&
    !node.is_group?.('equals')
  ) {
    node = node.children[0];
  }
  if (!node.is_group?.('func')) return null;
  if (node.value === 'ln') {
    if (baseAscii !== 'e') return null;
    const arg = node.get_arg?.();
    return arg ? arg.to_ascii() : null;
  }
  if (node.value === 'log') {
    // Check subscript base
    const label = node.children?.[0];
    const ss = label?.get_script?.('subscript')?.children?.[0];
    const logBase = ss ? ss.to_ascii() : '10';
    if (logBase !== baseAscii) return null;
    const arg = node.get_arg?.();
    return arg ? arg.to_ascii() : null;
  }
  return null;
}

/**
 * Check if a model node is a power with the given base.
 * Returns the exponent's ASCII if it matches, or null.
 * Unwraps container nodes (AlgebraModel, brackets) to find the power.
 */
function extractExponentIfBaseMatches(node, baseAscii) {
  if (!node) return null;
  // Unwrap single-child containers (AlgebraModel, brackets, etc.)
  while (
    node.children?.length === 1 &&
    !node.is_group?.('power') &&
    !node.is_group?.('equals')
  ) {
    node = node.children[0];
  }
  if (!node.is_group?.('power')) return null;
  const powerBase = node.children?.[0];
  if (!powerBase || powerBase.to_ascii() !== baseAscii) return null;
  // The exponent is children[1], which is an Exponent wrapper containing the actual content
  const exponent = node.children?.[1];
  if (!exponent) return null;
  const expContent = exponent.is_group?.('exponent') ? exponent.children?.[0] : exponent;
  return expContent ? expContent.to_ascii() : null;
}

/**
 * Find the Equals node in a model, unwrapping containers.
 */
function findEqualsNode(model) {
  if (!model) return null;
  if (model.is_group?.('equals')) return model;
  if (model.children) {
    for (const child of model.children) {
      const found = findEqualsNode(child);
      if (found) return found;
    }
  }
  return null;
}

/**
 * "Shift to Log Space": Extract exponents when all terms are powers with the
 * same base (shift down a level). Otherwise wrap in log_b(...).
 */
function bopToLogSpace() {
  const currentAscii = getCurrentExpressionAscii();
  if (!currentAscii) {
    showInfoMessage('Create an expression first.');
    return;
  }

  const useModal = document.getElementById('setting-bop-modal')?.checked;
  if (useModal) {
    bopViaModal(currentAscii, 'log');
    return;
  }

  const base = resolveBopBase();

  // Try smart shift: if all terms are b^{...}, just extract exponents
  const derivation = getLatestDerivation();
  const model = derivation?.getLastRow()?.model;
  if (model && currentAscii.includes('=')) {
    const eqNode = findEqualsNode(model);
    if (eqNode && eqNode.children?.length >= 2) {
      // Equals node has 3 children: [LHS, '=' operator, RHS]
      const lhsNode = eqNode.children[0];
      const rhsNode = eqNode.children[eqNode.children.length - 1];
      const lhsExp = extractExponentIfBaseMatches(lhsNode, base);
      const rhsExp = extractExponentIfBaseMatches(rhsNode, base);
      if (lhsExp !== null && rhsExp !== null) {
        addChainStep(lhsExp + '=' + rhsExp, 'Shift to Log Space', 'log');
        return;
      }
    }
  }

  // Fallback: wrap in log_b(...)
  const logFunc = base === 'e' ? 'ln' : 'log_' + base;
  let newAscii;
  if (currentAscii.includes('=')) {
    const [lhs, rhs] = currentAscii.split('=').map((s) => s.trim());
    newAscii = logFunc + '(' + lhs + ')=' + logFunc + '(' + rhs + ')';
  } else {
    newAscii = logFunc + '(' + currentAscii + ')';
  }

  addChainStep(newAscii, 'Shift to Log Space', 'log');
}

/**
 * Open a modal for students to construct the bop wrapping themselves.
 * The modal loads the current expression and lets students manipulate it.
 */
function bopViaModal(currentAscii, targetSpace) {
  const base = resolveBopBase();
  const direction =
    targetSpace === 'exp'
      ? 'Wrap in ' + base + '^{...} to shift to Exp Space'
      : 'Wrap in ' +
        (base === 'e' ? 'ln(...)' : 'log_' + base + '(...)') +
        ' to shift to Log Space';

  const modal = new ModalCanvas({
    title: 'Shift to ' + (targetSpace === 'exp' ? 'Exp' : 'Log') + ' Space',
    affordance: 'bop-modal',
    expressions: [{ ascii: currentAscii, position: { x: 40, y: 30 } }],
    instructions:
      '<p>' +
      direction +
      '</p><p>Manipulate the expression, then press <strong>Done</strong>.</p>',
    doneButton: { initialState: 'done' },
    onDone: (ctx) => {
      const derivations = ctx.derivations;
      if (!derivations || derivations.length === 0) return;
      try {
        const lastDl = derivations[derivations.length - 1];
        const resultAscii = lastDl.getLastRow().model.to_ascii();
        if (resultAscii && resultAscii !== currentAscii) {
          addChainStep(
            resultAscii,
            'Shift to ' + (targetSpace === 'exp' ? 'Exp' : 'Log') + ' Space',
            targetSpace
          );
        }
      } catch (e) {
        /* ignore */
      }
    },
  });
  modal.open();
}

// ========== BUTTON STATE MANAGEMENT ==========
function updateButtonStates() {
  if (!affordanceSidebar) return;

  const hasExpression = expressions.length > 0 || !!chainManager?.originalExpression;
  const hasSteps = chainManager ? chainManager.steps.length > 0 : false;

  // Create Expression - always enabled, label changes
  affordanceSidebar.enable('create-expression');
  const createBtn = affordanceSidebar.get('create-expression');
  if (createBtn) {
    const labelSpan = createBtn.querySelector('.card-label');
    if (labelSpan) {
      labelSpan.textContent = hasExpression ? 'New Expression' : 'Create Expression';
    }
  }

  // Bop buttons — enabled when expression exists and is an equation
  const currentAscii = getCurrentExpressionAscii();
  const isEquation = currentAscii?.includes('=');
  const inLogSpace = currentSpace === 'log';
  const inExpSpace = currentSpace === 'exp';

  // Detect base and update floating bop button labels
  const zoneBopExp = document.getElementById('zone-bop-to-exp');
  const zoneBopLog = document.getElementById('zone-bop-to-log');
  if (hasExpression) {
    const base = resolveBopBase();
    if (zoneBopExp) {
      const baseDisplay = base === 'e' ? 'e' : base;
      zoneBopExp.innerHTML =
        baseDisplay + '<sup>x</sup> <span class="zone-bop-arrow">&rarr;</span>';
    }
    if (zoneBopLog) {
      const logDisplay =
        base === 'e' ? 'ln' : base === '10' ? 'log' : 'log<sub>' + base + '</sub>';
      zoneBopLog.innerHTML = '<span class="zone-bop-arrow">&larr;</span> ' + logDisplay;
    }
  } else {
    // Revert to generic labels
    if (zoneBopExp)
      zoneBopExp.innerHTML = 'b<sup>x</sup> <span class="zone-bop-arrow">&rarr;</span>';
    if (zoneBopLog)
      zoneBopLog.innerHTML = '<span class="zone-bop-arrow">&larr;</span> log<sub>b</sub>';
  }

  // Move bop buttons to follow expression
  const bopContainer = document.getElementById('zone-bop-buttons');
  if (bopContainer) {
    if (!hasExpression) bopContainer.style.left = '50%';
    else if (inLogSpace) bopContainer.style.left = '25%';
    else bopContainer.style.left = '75%';
  }

  // Shift to Exp Space: enabled when expression exists and currently in log space (or double bop allowed)
  const allowDouble =
    document.getElementById('setting-allow-double-bop')?.checked ?? false;
  const expEnabled = hasExpression && (inLogSpace || allowDouble);
  if (expEnabled) {
    affordanceSidebar.enable('bop-to-exp');
    affordanceSidebar.get('bop-to-exp')?.removeAttribute('title');
  } else {
    affordanceSidebar.disable('bop-to-exp');
    affordanceSidebar
      .get('bop-to-exp')
      ?.setAttribute(
        'title',
        !hasExpression ? 'Create an expression first' : 'Already in Exp Space'
      );
  }
  if (zoneBopExp) zoneBopExp.disabled = !expEnabled;

  // Shift to Log Space: enabled when expression exists and currently in exp space (or double bop allowed)
  const logEnabled = hasExpression && (inExpSpace || allowDouble);
  if (logEnabled) {
    affordanceSidebar.enable('bop-to-log');
    affordanceSidebar.get('bop-to-log')?.removeAttribute('title');
  } else {
    affordanceSidebar.disable('bop-to-log');
    affordanceSidebar
      .get('bop-to-log')
      ?.setAttribute(
        'title',
        !hasExpression ? 'Create an expression first' : 'Already in Log Space'
      );
  }
  if (zoneBopLog) zoneBopLog.disabled = !logEnabled;

  // Rewrite formula
  if (hasExpression) {
    affordanceSidebar.enable('rewrite-formula');
    affordanceSidebar.get('rewrite-formula')?.removeAttribute('title');
  } else {
    affordanceSidebar.disable('rewrite-formula');
    affordanceSidebar
      .get('rewrite-formula')
      ?.setAttribute('title', 'Create an expression first');
  }

  // Undo
  if (hasSteps) {
    affordanceSidebar.enable('undo-step');
    affordanceSidebar.get('undo-step')?.removeAttribute('title');
  } else {
    affordanceSidebar.disable('undo-step');
    affordanceSidebar.get('undo-step')?.setAttribute('title', 'No steps to undo');
  }

  // Reset
  if (hasSteps) {
    affordanceSidebar.enable('reset');
    affordanceSidebar.get('reset')?.removeAttribute('title');
  } else {
    affordanceSidebar.disable('reset');
    affordanceSidebar.get('reset')?.setAttribute('title', 'No steps to reset');
  }

  checkFullySimplified();
  setTimeout(() => updateClickOverlays(), 100);
}

// ========== SUCCESS DETECTION ==========
function checkFullySimplified() {
  const indicator = document.getElementById('success-indicator');
  if (!indicator) return;

  const hasExpression = !!chainManager?.originalExpression;
  if (!hasExpression || chainManager.steps.length === 0) {
    indicator.classList.remove('visible');
    return;
  }

  // Get latest model
  const latestStep = chainManager.steps[chainManager.steps.length - 1];
  const ascii = latestStep?.ascii || chainManager.getCurrentAscii();
  if (!ascii) return;

  // Check for remaining log/exp actions on the expression
  const derivation = latestStep?.derivation || expressions[0]?.derivation;
  if (!derivation) return;
  const model = derivation.getLastRow()?.model;
  if (!model) return;

  // For Convert presets, success = reaching the goal space
  const activePreset = activePresetId ? PAGE_PRESETS[activePresetId] : null;
  if (activePreset?.goalSpace && currentSpace === activePreset.goalSpace) {
    indicator.classList.add('visible');
    if (!completedSteps.has('simplified')) {
      GuidedTopics.triggerCelebration?.();
    }
    completedSteps.add('simplified');
    return;
  }

  // For other presets, success = no remaining log/exp actions
  const actionNames = [
    'LogarithmEvaluateAction',
    'LogarithmEvaluateSameArgAsBaseAction',
    'LogSimplifyAction',
    'LogSimplifyPowerAction',
    'LogarithmInvertAction',
  ];

  const hasRemaining = hasRemainingOpportunities(model, actionNames);
  if (!hasRemaining) {
    indicator.classList.add('visible');
    if (!completedSteps.has('simplified')) {
      GuidedTopics.triggerCelebration?.();
    }
    completedSteps.add('simplified');
  } else {
    indicator.classList.remove('visible');
    completedSteps.delete('simplified');
  }
}

// hasRemainingOpportunities imported from GuidedTopics.ChainState

// ========== UNDO / RESET ==========
function undoLastStep() {
  if (!chainManager || chainManager.steps.length === 0) return;

  // Before undo, check if we need to update space tracking
  chainManager.undoLast();

  // Recalculate current space from remaining state
  if (chainManager.steps.length > 0) {
    const lastStep = chainManager.steps[chainManager.steps.length - 1];
    currentSpace = lastStep.space || detectSpace(lastStep.ascii);
  } else if (expressions.length > 0) {
    currentSpace = expressions[0].space || detectSpace(chainManager.originalExpression);
  }

  document.getElementById('success-indicator')?.classList.remove('visible');
  updateButtonStates();
}

function resetToOriginal() {
  chainManager?.clearAll();
  document.getElementById('success-indicator')?.classList.remove('visible');

  // Reset to original expression's space
  if (expressions.length > 0) {
    currentSpace = expressions[0].space || detectSpace(chainManager?.originalExpression);
  }

  updateButtonStates();
}

// ========== EXPRESSION WIZARD ==========
let wizardOverlay = null;

function openExpressionWizard() {
  if (!wizardOverlay) {
    wizardOverlay = buildWizardOverlay();
  }
  wizardOverlay.classList.add('active');
  const input = wizardOverlay.querySelector('input');
  if (input) {
    input.value = '';
    setTimeout(() => input.focus(), 50);
  }
  document.addEventListener('keydown', wizardEscHandler);
}

function closeWizard() {
  if (wizardOverlay) wizardOverlay.classList.remove('active');
  document.removeEventListener('keydown', wizardEscHandler);
  if (!expressions.length) startChooser?.show();
}

function wizardEscHandler(e) {
  if (e.key === 'Escape') closeWizard();
}

function buildWizardOverlay() {
  const overlay = document.createElement('div');
  overlay.className = 'overlay';

  const content = document.createElement('div');
  content.className = 'overlay-content';
  content.style.maxWidth = '480px';

  // Header
  const header = document.createElement('div');
  header.className = 'overlay-header';
  const title = document.createElement('h2');
  title.className = 'overlay-title';
  title.textContent = 'Create Expression';
  const closeBtn = document.createElement('button');
  closeBtn.className = 'close-button';
  closeBtn.innerHTML = '&times;';
  closeBtn.onclick = () => closeWizard();
  header.appendChild(title);
  header.appendChild(closeBtn);
  content.appendChild(header);

  // Instruction
  const instrP = document.createElement('p');
  instrP.className = 'modal-subtitle';
  instrP.textContent = 'Type a log or exponential expression:';
  content.appendChild(instrP);

  // Examples
  const hintP = document.createElement('p');
  hintP.style.cssText = 'font-size: 0.8rem; color: #6b7280; margin: 0 0 0.75rem 0;';
  hintP.innerHTML =
    'Examples: <code>log_3(81)</code>, <code>2^6 = 64</code>, <code>ln(e^5)</code>, <code>log(1000) = 3</code>';
  content.appendChild(hintP);

  // Input
  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'form-input';
  input.style.cssText =
    'width: 100%; font-size: 1.1rem; font-family: monospace; padding: 0.5rem;';
  input.placeholder = 'log_2(8) or 3^x = 27';
  content.appendChild(input);

  // Button row
  const btnRow = document.createElement('div');
  btnRow.style.cssText =
    'display: flex; justify-content: flex-end; gap: 0.5rem; margin-top: 1rem;';

  const createBtn = document.createElement('button');
  createBtn.className = 'btn btn-primary';
  createBtn.textContent = 'Create';
  createBtn.onclick = () => {
    const ascii = input.value.trim();
    if (ascii) {
      closeWizard();
      const space = detectSpace(ascii);
      createOriginalExpression(ascii, space);
    }
  };

  btnRow.appendChild(createBtn);
  content.appendChild(btnRow);

  overlay.appendChild(content);

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') createBtn.click();
  });

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) closeWizard();
  });

  document.body.appendChild(overlay);
  return overlay;
}

// ========== STATE SHARING ==========
function initStateSharing() {
  if (!GuidedTopics.StateSharing?.initStateSharing) return;

  GuidedTopics.StateSharing.initStateSharing({
    pageId: PAGE_ID,
    captureState: () => {
      if (!chainManager?.originalExpression) return null;
      return {
        original: chainManager.originalExpression,
        originalSpace: expressions[0]?.space || 'log',
        steps: chainManager.steps.map((s) => ({
          ascii: s.ascii,
          rule: s.rule,
          space: s.space,
        })),
        settings: {
          labels: document.getElementById('setting-show-labels')?.checked ?? true,
          allowLn: document.getElementById('setting-allow-ln')?.checked ?? true,
        },
      };
    },
    restoreState: async (s) => {
      if (s.settings?.labels !== undefined) {
        const el = document.getElementById('setting-show-labels');
        if (el) el.checked = s.settings.labels;
      }
      if (s.settings?.allowLn !== undefined) {
        const el = document.getElementById('setting-allow-ln');
        if (el) el.checked = s.settings.allowLn;
      }
      if (s.original) {
        createOriginalExpression(s.original, s.originalSpace || 'log');
        if (s.steps?.length) {
          for (const step of s.steps) {
            await new Promise((r) => setTimeout(r, 300));
            addChainStep(step.ascii, step.rule || '', step.space);
          }
        }
      }
      return true;
    },
    showMessage: showInfoMessage,
  });
}

// ========== TOPIC PAGE ==========
page = new GuidedTopics.TopicPage({
  pageId: PAGE_ID,
  title: 'Log & Exponential Form',
  subject: 'algebra',
  guideUrl: 'log-exponential-form-guide.html',
  settingsItems: [
    {
      type: 'checkbox',
      id: 'setting-show-labels',
      label: 'Show Step Labels',
      checked: true,
      title: 'Display the action name (e.g. "Convert to Exp Form") after each step.',
    },
    {
      type: 'checkbox',
      id: 'setting-allow-ln',
      label: 'Allow Natural Log (ln)',
      checked: true,
      title:
        'Include presets with ln notation. When off, only log and log_b presets are shown.',
    },
    {
      type: 'checkbox',
      id: 'setting-bop-modal',
      label: 'Bop via Modal',
      checked: false,
      title:
        'Require students to construct the wrapping expression in a modal instead of auto-wrapping.',
    },
    {
      type: 'checkbox',
      id: 'setting-allow-double-bop',
      label: 'Allow Double Wrap',
      checked: false,
      title:
        'Enable wrapping expressions that are already in that space (e.g., double exponentiate or double log).',
    },
  ],
  affordances: [
    { key: 'create-expression', handler: () => startChooser?.show() },
    { type: 'divider' },
    { key: 'bop-to-exp', handler: () => bopToExpSpace() },
    { key: 'bop-to-log', handler: () => bopToLogSpace() },
    { key: 'set-base', handler: () => promptSetBase() },
    { key: 'rewrite-formula', handler: () => rewriteFlow?.open() },
    { type: 'divider' },
    { key: 'undo-step', handler: () => undoLastStep() },
    { key: 'reset', handler: () => resetToOriginal() },
  ],
  updateButtonStates: () => updateButtonStates(),
  setupHints: false,

  initCanvas: (ctx) => {
    affordanceSidebar = ctx.affordanceSidebar;
    const result = GuidedTopics.createChainPageCanvas({
      expressions,
      completedSteps,
      updateButtonStates,
      setupExpressionFromPreset,
      PAGE_PRESETS,
      chainManagerConfig: {
        startY: 120,
        stepSpacing: STEP_SPACING,
        posX: getLogSpaceX(),
        colors: [
          { r: 59, g: 130, b: 246 }, // blue
          { r: 16, g: 185, b: 129 }, // green
          { r: 245, g: 158, b: 11 }, // amber
          { r: 139, g: 92, b: 246 }, // purple
          { r: 236, g: 72, b: 153 }, // pink
        ],
      },
      setupHints: false,
      setupRewriteFlow: true,
      skipEqParam: true, // Page handles ?eq= itself with space detection
      onRewriteResult: (expr, formName, resultAscii) => {
        // Stay in the current space after rewriting (don't re-detect)
        addChainStep(resultAscii, 'Rewrite', currentSpace);
      },
      onStepAdded: () => {
        checkFullySimplified();
      },
      onStepRemoved: () => {
        document.getElementById('success-indicator')?.classList.remove('visible');
      },
      onReset: () => {},
      onCanvasCleared: () => {
        document.getElementById('success-indicator')?.classList.remove('visible');
        markCanvasEmpty();
        startChooser?.show();
      },
      afterPresetLoaded: () => {
        startChooser?.hide();
      },
    });
    canvas = result.canvas;
    chainManager = result.chainManager;
    rewriteFlow = result.rewriteFlow;
    return canvas;
  },

  onReady: (ctx) => {
    // Settings event listeners
    document.getElementById('setting-show-labels')?.addEventListener('change', (e) => {
      const show = e.target.checked;
      document.querySelectorAll('#gm-page-container .step-reason').forEach((el) => {
        el.style.display = show ? '' : 'none';
      });
    });

    document.getElementById('setting-allow-ln')?.addEventListener('change', () => {
      updateButtonStates();
    });

    document
      .getElementById('setting-allow-double-bop')
      ?.addEventListener('change', () => {
        updateButtonStates();
      });

    startChooser = new StartChooser({
      presets: PAGE_PRESETS,
      categories: [
        {
          label: 'Convert to Log Form',
          presetIds: ['preset-to-log-basic', 'preset-to-log-neg'],
        },
        {
          label: 'Convert to Exp Form',
          presetIds: ['preset-to-exp-basic', 'preset-to-exp-common'],
        },
        {
          label: 'Evaluate',
          presetIds: [
            'preset-eval-integer',
            'preset-eval-common',
            'preset-eval-negative',
            'preset-eval-fraction',
          ],
        },
        {
          label: 'Special Values',
          presetIds: [
            'preset-special-zero',
            'preset-special-identity',
            'preset-special-inverse',
            'preset-special-inverse-exp',
          ],
        },
      ],
      container: document.querySelector('.canvas-wrapper'),
      title: 'Choose an expression to work with',
      hideCreateOwn: false,
      onPresetSelect: (presetId) => {
        setupExpressionFromPreset(presetId);
      },
      onCreateOwn: () => openExpressionWizard(),
    });
    // Check for ?eq= parameter — load equation directly, skip chooser
    const eqParam = new URLSearchParams(window.location.search).get('eq');
    if (eqParam) {
      const space = detectSpace(eqParam);
      // Base will be detected from the model tree after expression loads
      setTimeout(() => createOriginalExpression(eqParam, space), 200);
    } else {
      startChooser.show();
    }

    // Wire floating zone bop buttons
    document
      .getElementById('zone-bop-to-exp')
      ?.addEventListener('click', () => bopToExpSpace());
    document
      .getElementById('zone-bop-to-log')
      ?.addEventListener('click', () => bopToLogSpace());

    initStateSharing();

    // Reposition expressions and labels on resize
    window.addEventListener('resize', () => {
      repositionAllExpressions();
    });

    // Click anywhere on a derivation to open Rewrite Formula.
    // Transparent click-catcher overlays are placed over each expression.
    // Label button clicks also open the rewrite flow.
    const canvasContainer = document.getElementById('gm-page-container');
    if (canvasContainer) {
      canvasContainer.addEventListener('click', (e) => {
        if (
          e.target.closest('.expression-label-btn') &&
          expressions.length > 0 &&
          rewriteFlow
        ) {
          rewriteFlow.open();
        }
        if (e.target.closest('.expression-click-overlay') && rewriteFlow) {
          rewriteFlow.open();
        }
      });
    }

    // Cleanup on page exit
    window.addEventListener('beforeunload', () => {
      chainManager?.clearAll?.();
    });

    setTimeout(() => {
      updateButtonStates();
    }, 150);
  },
}).init();
